import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { ApiError, requisitar } from '../../shared/api/client.ts';
import type { Sessao } from '../../shared/api/tipos.ts';
import { useSessao } from './sessao.tsx';
import { Logo } from '../../shared/ui/Logo.tsx';

export function Login() {
  const { entrar } = useSessao();
  const navegar = useNavigate();
  const localizacao = useLocation();

  // `RotaProtegida` guarda aqui o endereço que a pessoa tentou abrir antes de
  // ser mandada para o login, para o login devolver ela no lugar certo.
  const destino = (localizacao.state as { de?: string } | null)?.de ?? '/clientes';

  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);

  const autenticar = useMutation({
    mutationFn: (credenciais: { usuario: string; senha: string }) =>
      requisitar<Sessao>('/auth/login', {
        metodo: 'POST',
        corpo: credenciais,
        // 401 aqui é "senha errada", não sessão perdida.
        trataProprio401: true,
      }),
    onSuccess: (sessao) => {
      entrar(sessao);
      navegar(destino, { replace: true });
    },
  });

  const podeEnviar = usuario.trim() !== '' && senha !== '' && !autenticar.isPending;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;
    autenticar.mutate({ usuario: usuario.trim(), senha });
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack component="form" onSubmit={enviar} spacing={2.5}>
            <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
              <Logo altura={104} />
              <Box>
                <Typography variant="h2" sx={{ lineHeight: 1.2 }}>
                  Contábil RK Solutions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Entre para acessar clientes e contabilidades.
                </Typography>
              </Box>
            </Stack>

            {autenticar.isError && (
              <Alert severity="error">{mensagemDoErro(autenticar.error)}</Alert>
            )}

            <TextField
              label="Usuário"
              value={usuario}
              onChange={(evento) => setUsuario(evento.target.value)}
              required
              autoFocus
              fullWidth
              autoComplete="username"
              slotProps={{
                htmlInput: {
                  maxLength: 40,
                  // O gerenciador de senhas do navegador e o corretor do
                  // celular atrapalham num campo de login: um sugere e-mail,
                  // o outro põe maiúscula na primeira letra.
                  autoCapitalize: 'none',
                  autoCorrect: 'off',
                  spellCheck: false,
                },
              }}
            />

            <TextField
              label="Senha"
              type={mostrarSenha ? 'text' : 'password'}
              value={senha}
              onChange={(evento) => setSenha(evento.target.value)}
              required
              fullWidth
              autoComplete="current-password"
              slotProps={{
                htmlInput: { maxLength: 200 },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setMostrarSenha((atual) => !atual)}
                        aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                        edge="end"
                        size="small"
                      >
                        {mostrarSenha ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={!podeEnviar}
              loading={autenticar.isPending}
            >
              Entrar
            </Button>

            <Typography variant="caption" color="text.secondary">
              Esqueceu a senha? Peça ao administrador para redefini-la.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) {
    // 429 vem do limite de tentativas do servidor e não tem corpo de domínio.
    if (erro.status === 429) {
      return 'Muitas tentativas. Espere um minuto e tente de novo.';
    }
    return erro.message;
  }
  return 'Não foi possível entrar. Verifique se o servidor está no ar.';
}
